/**
 * goldFly — tiny shared "gold coin + amount floats up" beat.
 *
 * Used by the crew daily-gift button (on send) and the notification inbox
 * COLLECT buttons (on collect). The element lives on document.body (fixed)
 * so list re-renders can't kill it mid-flight, and the styles are injected
 * here so neither caller depends on the other's stylesheet.
 */

import { sound, SoundId } from '../../engine/audio/sound';

const STYLE_ID = 'gold-fly-styles';

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
.gold-coin-fly {
  position: fixed;
  z-index: 1200;
  display: flex;
  align-items: center;
  gap: 4px;
  pointer-events: none;
  transform: translate(-50%, 0);
}
.gold-coin-fly img {
  width: 22px;
  height: 22px;
}
.gold-coin-fly span {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 16px;
  color: var(--gold, #FFD700);
  text-shadow: 0 0 8px rgba(255, 215, 0, 0.5);
}
`;
  document.head.appendChild(style);
}

/**
 * Fly a gold coin with "+amount" up from the centre of `fromEl` and fade it
 * out. No-op when the element isn't actually visible (zero rect) — e.g. the
 * twin cell in a hidden tab.
 *
 * `sfx` picks the chime: GOLD_COLLECT (default) when the player receives gold,
 * GOLD_SEND when they give it away (crew daily gift).
 */
const FLY_DURATION = 850;

export function flyGoldCoin(
  fromEl: HTMLElement,
  amount = 10,
  sfx: SoundId.GOLD_COLLECT | SoundId.GOLD_SEND = SoundId.GOLD_COLLECT,
): void {
  const rect = fromEl.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return;
  ensureStyles();

  // One coin chime per flyout — this function IS the gold beat, so the sound
  // lives here and every caller gets it for free. Fires on launch, in sync
  // with the tap. The zero-rect bail above keeps a hidden twin cell silent.
  sound.play(sfx);

  const fly = document.createElement('div');
  fly.className = 'gold-coin-fly';
  fly.innerHTML = `<img src="/assets/icons/currency/gold.svg" alt=""><span>+${amount}</span>`;
  fly.style.left = `${rect.left + rect.width / 2}px`;
  fly.style.top = `${rect.top}px`;
  document.body.appendChild(fly);

  const anim = fly.animate(
    [
      // Simple beat, played constantly: rise to its resting height at full
      // opacity, then fade out in place. Scale stays 1 throughout — no pop.
      { transform: 'translate(-50%, 0)', opacity: 1 },
      { transform: 'translate(-50%, -30px)', opacity: 1, offset: 0.55 },
      { transform: 'translate(-50%, -30px)', opacity: 0, offset: 1 },
    ],
    { duration: FLY_DURATION, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'both' },
  );

  anim.onfinish = () => fly.remove();
}
